import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, CreditCard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;

    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        const data = res.data;
        setPaymentData(data);
        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    };
    pollStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background" data-testid="payment-success-page">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-16">
        <Card className="border-2">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              {status === 'checking' && <Loader2 className="h-16 w-16 text-orange-500 animate-spin" />}
              {status === 'success' && <CheckCircle2 className="h-16 w-16 text-green-500" />}
              {(status === 'error' || status === 'expired' || status === 'timeout') && <XCircle className="h-16 w-16 text-red-500" />}
            </div>
            <CardTitle className="text-xl" data-testid="payment-status-title">
              {status === 'checking' && 'Zahlung wird verarbeitet...'}
              {status === 'success' && 'Zahlung erfolgreich!'}
              {status === 'error' && 'Zahlungsfehler'}
              {status === 'expired' && 'Zahlungssitzung abgelaufen'}
              {status === 'timeout' && 'Status-Abfrage Timeout'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {status === 'checking' && (
              <p className="text-muted-foreground">Bitte warten Sie, Ihr Zahlungsstatus wird überprüft...</p>
            )}
            {status === 'success' && (
              <>
                <p className="text-muted-foreground">Ihre Subscription wurde erfolgreich aktiviert.</p>
                {paymentData && (
                  <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4 text-sm">
                    <p className="text-green-700 dark:text-green-400">
                      Betrag: €{(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </p>
                  </div>
                )}
              </>
            )}
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <p className="text-muted-foreground">
                {status === 'expired' ? 'Die Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut.' :
                 status === 'timeout' ? 'Die Status-Überprüfung hat zu lange gedauert. Bitte prüfen Sie Ihre E-Mails.' :
                 'Es gab einen Fehler bei der Zahlung. Bitte versuchen Sie es erneut.'}
              </p>
            )}
            <div className="flex gap-3 justify-center pt-2">
              <Button onClick={() => navigate('/subscriptions')} data-testid="back-to-subscriptions-btn">
                <CreditCard className="h-4 w-4 mr-2" />Zur Subscription-Verwaltung
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
